import React, { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import api from "../lib/api";
import { SERVICE_OPTIONS, US_STATES } from "../lib/constants";
import { CalendlyInline } from "./Calendly";

export default function ConsultationForm({ compact = false }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    state: "",
    service: "",
    client_type: "Individual",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.state || !form.service) {
      toast.error("Please fill all required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      toast.success("Consultation request received. We will reach out within 1 business day.");
      setForm({ full_name: "", email: "", phone: "", state: "", service: "", client_type: "Individual", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="consultation-success" className="bg-white border border-[#cbd5e1] rounded-2xl p-6 sm:p-10">
        <div className="text-center mb-8">
          <CheckCircle2 className="w-14 h-14 text-[#1e6dd8] mx-auto mb-4" />
          <h3 className="font-serif-hsa text-2xl font-semibold text-[#0a2540] mb-2">Request Received</h3>
          <p className="text-slate-600 max-w-lg mx-auto">
            Thank you. An HSA Service Hub advisor will reach out within one business day.
            <span className="block mt-2 text-[#0a2540] font-medium">Want to pick a time right now?</span>
          </p>
        </div>
        <div className="mb-6">
          <CalendlyInline height={620} />
        </div>
        <div className="text-center">
          <Button
            data-testid="consultation-submit-another"
            variant="outline"
            className="border-[#0a2540] text-[#0a2540]"
            onClick={() => setDone(false)}
          >
            Submit Another Request
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="consultation-form" className="bg-white border border-[#cbd5e1] rounded-2xl p-6 sm:p-10 shadow-[0_8px_30px_rgb(10,37,64,0.06)]">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <Label className="text-[#0a2540] font-medium">Full Name *</Label>
          <Input
            data-testid="lead-input-full-name"
            value={form.full_name}
            onChange={(e) => set("full_name", e.target.value)}
            placeholder="Jane Doe"
            className="mt-1.5 h-11 border-[#cbd5e1] focus-visible:ring-[#1e6dd8]"
          />
        </div>
        <div>
          <Label className="text-[#0a2540] font-medium">Email Address *</Label>
          <Input
            data-testid="lead-input-email"
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="you@example.com"
            className="mt-1.5 h-11 border-[#cbd5e1] focus-visible:ring-[#1e6dd8]"
          />
        </div>
        <div>
          <Label className="text-[#0a2540] font-medium">Phone Number *</Label>
          <Input
            data-testid="lead-input-phone"
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            placeholder="+1 (555) 000-0000"
            className="mt-1.5 h-11 border-[#cbd5e1] focus-visible:ring-[#1e6dd8]"
          />
        </div>
        <div>
          <Label className="text-[#0a2540] font-medium">State *</Label>
          <Select value={form.state} onValueChange={(v) => set("state", v)}>
            <SelectTrigger data-testid="lead-select-state" className="mt-1.5 h-11 border-[#cbd5e1]">
              <SelectValue placeholder="Select your state" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {US_STATES.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[#0a2540] font-medium">Service Needed *</Label>
          <Select value={form.service} onValueChange={(v) => set("service", v)}>
            <SelectTrigger data-testid="lead-select-service" className="mt-1.5 h-11 border-[#cbd5e1]">
              <SelectValue placeholder="Choose a service" />
            </SelectTrigger>
            <SelectContent>
              {SERVICE_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[#0a2540] font-medium mb-2 block">Individual or Business *</Label>
          <RadioGroup
            value={form.client_type}
            onValueChange={(v) => set("client_type", v)}
            className="flex gap-4"
            data-testid="lead-radio-client-type"
          >
            <label className="flex items-center gap-2 border border-[#cbd5e1] rounded-lg px-4 py-2.5 cursor-pointer hover:border-[#1e6dd8] flex-1">
              <RadioGroupItem value="Individual" id="ct-individual" />
              <span className="text-sm font-medium text-slate-700">Individual / Family</span>
            </label>
            <label className="flex items-center gap-2 border border-[#cbd5e1] rounded-lg px-4 py-2.5 cursor-pointer hover:border-[#1e6dd8] flex-1">
              <RadioGroupItem value="Business" id="ct-business" />
              <span className="text-sm font-medium text-slate-700">Business / Organization</span>
            </label>
          </RadioGroup>
        </div>
        {!compact && (
          <div className="sm:col-span-2">
            <Label className="text-[#0a2540] font-medium">Message</Label>
            <Textarea
              data-testid="lead-input-message"
              value={form.message}
              onChange={(e) => set("message", e.target.value)}
              rows={4}
              placeholder="Briefly tell us how we can help."
              className="mt-1.5 border-[#cbd5e1] focus-visible:ring-[#1e6dd8]"
            />
          </div>
        )}
      </div>

      <Button
        data-testid="lead-submit-btn"
        type="submit"
        disabled={loading}
        className="w-full mt-7 h-12 bg-[#0a2540] hover:bg-[#1e6dd8] text-white font-semibold rounded-lg text-base"
      >
        {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>) : "Submit Consultation Request"}
      </Button>
      <p className="text-xs text-slate-500 mt-3 text-center">
        Your information is confidential and only used to coordinate your consultation.
      </p>
    </form>
  );
}
